/**
 * Utility Functions for Matters
 *
 * This module provides utility functions for matter-related operations,
 * including formatting, validation, and data transformations.
 */

import type { Matter, PostalAddress } from './types';

// ============================================================================
// Formatting Functions
// ============================================================================

/**
 * Format decedent's full name
 */
export function formatDecedentName(matter: Partial<Matter>): string {
  const parts = [
    matter.decedentTitle,
    matter.decedentFirstName,
    matter.decedentMiddleName,
    matter.decedentLastName
  ].filter(Boolean);

  return parts.length > 0 ? parts.join(' ') : 'Unnamed';
}

/**
 * Format postal address
 */
export function formatPostalAddress(address?: PostalAddress): string {
  if (!address) return '';

  const parts = [
    address.streetAddress,
    address.suburb,
    address.state,
    address.postcode,
    address.country
  ].filter(Boolean);

  return parts.join(', ');
}

// ============================================================================
// Validation Functions
// ============================================================================

/**
 * Whether the matter is being administered under intestacy (no will).
 *
 * The frontend reading of the backend's grant-type derivation (`deriveGrantType`,
 * VALE-1988), which the matter DTO does not expose: with an applicant LPR
 * (`applicant_for_letters_of_administration`), no will on file is Letters of
 * Administration on intestacy, and a will on file is Letters of Administration
 * with the Will, which is testate. `hasWill` is the backend's `willFileId`, the
 * same signal the derivation reads.
 *
 * Only a recorded applicant role reads as intestate. An Executor LPR, or no role
 * recorded yet, with no will on file is treated as a testate estate whose will
 * has not been uploaded, so the "will required" prompt still fires for it.
 */
export function isMatterIntestate(matter?: Partial<Matter> | null): boolean {
  if (matter?.hasWill) return false;
  return Boolean(
    matter?.legalPersonalRepresentatives?.some(
      (lpr) =>
        lpr.legalPersonalRepresentativeRole ===
        'applicant_for_letters_of_administration'
    )
  );
}
